import React from "react";
import dotBlue from "../../../../assets/brand/onboarding/dot-blue-7.svg";
import dotGreen from "../../../../assets/brand/onboarding/dot-green-7.svg";
import dotYellow from "../../../../assets/brand/onboarding/dot-yellow-7.svg";
import dotOrange from "../../../../assets/brand/onboarding/dot-orange-7.svg";
import radioChecked20 from "../../../../assets/brand/onboarding/radio-checked-20.svg";
import radioChecked18 from "../../../../assets/brand/onboarding/radio-checked-18.svg";
import spinnerArc from "../../../../assets/brand/onboarding/spinner-arc.svg";
import checkSmall from "../../../../assets/brand/onboarding/check-11x8.svg";
import spinnerArc96 from "../../../../assets/brand/onboarding/spinner-arc-96.svg";
import check28 from "../../../../assets/brand/onboarding/check-28x20.svg";
import warningTriangle24 from "../../../../assets/brand/onboarding/warning-triangle-24.svg";
import lock13 from "../../../../assets/brand/onboarding/lock-13x16.svg";
import { SegmentedControl } from "../../../auth/v2/pieces";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";
import { MobileCard } from "../../../ForBrands/ui/mobile/MobileUI";

// Pieces for the onboarding result screens, sized from the Figma frames:
// the hero band, the ScoreReveal card, metric rows, option and plan cards,
// the segmented control, toggles and the spinner.
//
// On phones (inside AuthFrame's phone theme, below 640px) the result pieces
// follow ATHLETE MODE V2 (mobile): the white hero with the 72px rounded
// avatar (1693:1264), the compact CacheScore card plus a separate halves
// card (2276:8394, 2276:8422), and fixed-width controls that share the row.
// 640px and up renders exactly as before.

export const initialsOf = (first = "", last = "") => `${(first || "")[0] || ""}${(last || "")[0] || ""}`.toUpperCase() || "?";

export const DOTS = { blue: dotBlue, green: dotGreen, yellow: dotYellow, orange: dotOrange };
// "grey" is the neutral dot of the phone badges (Figma #B8B8BC, bm-tertiary).
export const Dot = ({ color = "green" }) => (color === "grey"
  ? <span aria-hidden="true" className="h-[7px] w-[7px] shrink-0 rounded-full bg-bm-tertiary" />
  : <img src={DOTS[color] || dotGreen} alt="" className="h-[7px] w-[7px]" />);

// Outline badge, 22px tall, optional 7px dot.
export const OutlineBadge = ({ dot, children }) => (
  <span className="inline-flex h-[22px] items-center gap-[6px] rounded-full border border-bm-strong px-[9px] bm-label-xs text-bm-ink">
    {dot && <Dot color={dot} />}{children}
  </span>
);
// Dark badge, 22px. `upper={false}` keeps the text as written (the phone
// metric rows read "Limited").
export const DarkBadge = ({ upper = true, children }) => <span className={`inline-flex h-[22px] items-center rounded-full bg-bm-ink px-[9px] bm-label-xs ${upper ? "uppercase " : ""}text-white`}>{children}</span>;

export const WarnIcon = () => (
  <svg width="16" height="15" viewBox="0 0 16 15" aria-hidden="true"><path d="M8 1l7 12.5H1L8 1z" fill="#edd200" /><path d="M8 5.5v3.7M8 11v.6" stroke="#2c2c2c" strokeWidth="1.4" strokeLinecap="round" /></svg>
);
// The 24px outlined warning triangle of the phone frames (1760:4236).
export const WarnTriangle = () => <img src={warningTriangle24} alt="" className="h-6 w-6 shrink-0" />;
// The 13x16 lock of the phone "Unlock later" rows (1690:1250).
export const LockIcon = () => <img src={lock13} alt="" className="h-4 w-[13px] shrink-0" />;

// Desktop: a grey gradient band with a 64px round avatar. Phones (1693:1264,
// 1692:1358): a white band, a 72px rounded square with an orange border and
// orange initials (or the photo in the same square), 40px above and below.
export const HeroBand = ({ initials, avatar, badge, name, sub }) => {
  const phone = usePhoneTheme();
  if (phone) {
    return (
      <div className="bg-white px-6">
        <div className="flex items-center gap-4 py-10">
          {avatar ? (
            <img src={avatar} alt="" className="h-[72px] w-[72px] shrink-0 rounded-[16px] border border-bm-orange object-cover" />
          ) : (
            <span className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-[16px] border border-bm-orange bg-white bm-h-l text-bm-orange">{initials}</span>
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-[5px] break-words">
            {badge && <span><DarkBadge>{badge}</DarkBadge></span>}
            <h1 className="bm-h-l text-bm-ink">{name}</h1>
            {sub && <p className="bm-body-m text-bm-ink">{sub}</p>}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-gradient-to-b from-bm-strong to-white py-10">
      <div className="mx-auto flex w-full max-w-[880px] items-center gap-4 px-5">
        {avatar ? (
          <img src={avatar} alt="" className="h-16 w-16 rounded-full border border-bm-strong object-cover" />
        ) : (
          <span className="flex h-16 w-16 items-center justify-center rounded-full border border-bm-strong bg-white/10 bm-h-m text-bm-muted">{initials}</span>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
          {badge && <span><DarkBadge>{badge}</DarkBadge></span>}
          <h1 className="bm-h-l text-bm-ink">{name}</h1>
          {sub && <p className="bm-body-m text-bm-ink">{sub}</p>}
        </div>
      </div>
    </div>
  );
};

const GAUGE = 132;
const STROKE = 12;
// The CacheScore ring: 132px, stroke 12, a #f2f2f4 track, the number in
// bm-num-xl and a label under it. The compact phone card passes size 72,
// stroke 7, the #d9d9d9 track (2276:8402), the number in bm-h-l and no
// label (label={null}). A muted ring shows a dash instead of a number.
export const Gauge = ({ value, label = "of 100", muted = false, size = GAUGE, stroke = STROKE, track = "#f2f2f4", numberClass = "bm-num-xl text-bm-ink", dashClass = "h-[3px] w-6 rounded-full bg-bm-strong" }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke={track} strokeWidth={stroke} fill="none" />
        {!muted && <circle cx={size / 2} cy={size / 2} r={r} stroke="#FF4400" strokeWidth={stroke} fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} />}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {muted ? <span className={dashClass} /> : <span className={numberClass}>{Math.round(pct)}</span>}
        {label != null && label !== false && <span className="bm-overline !text-bm-tertiary">{label}</span>}
      </div>
    </div>
  );
};

// "Confidence High" / "Confidence Mid" / "Confidence Low" for the phone
// score card, from the stored CacheScore confidence ("high", "medium",
// "low", any case); null when there is none, so the badge is left out.
const CONFIDENCE = { high: "High", medium: "Mid", mid: "Mid", low: "Low" };
export const confidenceLabel = (state) => {
  const raw = String(state?.metrics?.confidenceFactor || "").trim().toLowerCase();
  if (!raw) return null;
  return `Confidence ${CONFIDENCE[raw] || `${raw.charAt(0).toUpperCase()}${raw.slice(1)}`}`;
};

const HALF_DOT = { on: "green", warn: "yellow", off: "blue" };

// Phones: the compact CacheScore card (card · CacheScore 2276:8394): the
// title in bm-h-s, a 10px sub, the confidence outline badge (left out when
// there is none) and the 72px ring on the right.
export const ScoreSummary = ({ score, muted = false, title, sub, confidence }) => (
  <MobileCard>
    <div className="flex items-center gap-3">
      <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
        <div className="flex w-full flex-col gap-[2px] break-words">
          <h2 className="bm-h-s text-bm-ink">{title}</h2>
          {sub && <p className="bm-body-xs text-bm-muted">{sub}</p>}
        </div>
        {confidence && <OutlineBadge>{confidence}</OutlineBadge>}
      </div>
      <div className="shrink-0">
        <Gauge value={score} muted={muted} label={null} size={72} stroke={7} track="#d9d9d9" numberClass="bm-h-l text-bm-ink" dashClass="h-[3px] w-[9px] rounded-full bg-bm-ink" />
      </div>
    </div>
  </MobileCard>
);

// Phones: the halves card under it (2276:8422). A half is { state: "on" |
// "warn" | "off", note, alert }; `alert` adds the 24px warning triangle
// (06b marks the missing Visibility half with it).
export const HalvesCard = ({ visibility, performance }) => (
  <div className="grid grid-cols-2 overflow-hidden rounded-[16px] border border-bm-border bg-white">
    {[["Visibility", visibility], ["Performance", performance]].map(([name, half], i) => (
      <div key={name} className={`flex min-w-0 flex-col gap-[5px] pb-[18px] pl-5 pr-4 pt-4 ${i === 0 ? "border-r border-bm-mutedbg" : ""}`}>
        <div className="flex h-8 items-center gap-[7px] bm-label-l text-bm-ink"><Dot color={HALF_DOT[half?.state] || "yellow"} />{name}{half?.alert && <WarnTriangle />}</div>
        <div className="bm-body-s text-bm-muted">{half?.note}</div>
      </div>
    ))}
  </div>
);

// Desktop: the ScoreReveal card, halves inside.
const ScoreReveal = ({ chip = "Core metrics", chipDot = "blue", score, ringLabel = "of 100", muted = false, title, sub, visibility, performance, highlight = true }) => (
  <div className={`w-full rounded-[24px] bg-white shadow-[0px_4px_16px_0px_rgba(0,0,0,0.06)] ${highlight ? "border-2 border-bm-orange" : "border border-bm-border"}`}>
    <div className="flex flex-col items-center px-6 py-[26px]">
      <OutlineBadge dot={chipDot}>{chip}</OutlineBadge>
      <div className="pb-[18px] pt-5"><Gauge value={score} label={muted ? "CacheScore" : ringLabel} muted={muted} /></div>
      <h2 className="text-center bm-h-m text-bm-ink">{title}</h2>
      {sub && <p className="pt-2 text-center bm-body-m text-bm-muted">{sub}</p>}
    </div>
    <div className="h-px w-full bg-bm-mutedbg" />
    <div className="grid grid-cols-2">
      {[["Visibility", visibility], ["Performance", performance]].map(([name, half], i) => (
        <div key={name} className={`flex flex-col gap-[5px] pb-[18px] pl-5 pr-4 pt-4 ${i === 0 ? "border-r border-bm-mutedbg" : ""}`}>
          <div className="flex h-8 items-center gap-[7px] bm-label-l text-bm-ink"><Dot color={HALF_DOT[half?.state] || "yellow"} />{name}{half?.state === "warn" && <WarnIcon />}</div>
          <div className="bm-body-s text-bm-muted">{half?.note}</div>
        </div>
      ))}
    </div>
  </div>
);

// The CacheScore card of the result screens. Desktop: the ScoreReveal card
// (`chip`, `chipDot`, `ringLabel`, `highlight`). Phones (D-o5): the compact
// card and the separate halves card, with the same title and sub plus the
// `confidence` badge (confidenceLabel(state)); a half's `alert` shows the
// warning triangle there. The phone props are ignored on desktop.
export const ScoreCard = ({ confidence, ...props }) => {
  const phone = usePhoneTheme();
  if (!phone) return <ScoreReveal {...props} />;
  const { score, muted = false, title, sub, visibility, performance } = props;
  return (
    <>
      <ScoreSummary score={score} muted={muted} title={title} sub={sub} confidence={confidence} />
      <HalvesCard visibility={visibility} performance={performance} />
    </>
  );
};

// A titled section: overline, 10px gap, content. `tertiary` greys the
// overline (the phone "Unlock later" section, 1690:1250).
export const Section = ({ label, right, tertiary = false, children }) => (
  <div className="flex flex-col gap-[10px]">
    <div className="flex items-center justify-between">
      <span className={tertiary ? "bm-overline !text-bm-tertiary" : "bm-overline"}>{label}</span>
      {right && <span className="bm-body-s text-bm-tertiary">{right}</span>}
    </div>
    {children}
  </div>
);
export const SectionLabel = ({ children, right }) => (
  <div className="flex items-center justify-between"><span className="bm-overline">{children}</span>{right && <span className="bm-body-s text-bm-tertiary">{right}</span>}</div>
);

// Metric rows in a 16px card: 105px rows, label and small sub on the left,
// a numeric on the right (or a badge and warning).
//
// Opt-in props for the phone frames (the defaults render as before):
// - tone "subtle": the grey list of 06b (1693:1305).
// - textWidth: the text column's width in px (200 on 06b, so the subs wrap
//   as drawn); it still shrinks on a narrower screen.
// - badgeUpper={false}: badges as written ("Limited", not "LIMITED").
// - locked: the "Unlock later" rows (1690:1250): tertiary text, 99px rows
//   and a lock in place of the chevron.
// Row fields besides title / sub / value / badge / warn / chevron:
// - alert: the 24px outlined warning triangle (06b).
// - pairs: [{ label, value }], the Contribution split in a 122px column
//   (1690:1234).
// - fits: category names, each with a 10px green dot (1690:1244; the
//   bm-positive green of the other status dots).
export const RowList = ({ rows, muted = false, tone = "white", textWidth, badgeUpper = true, locked = false }) => (
  <div className={`rounded-[16px] border border-bm-border ${tone === "subtle" ? "bg-bm-subtle" : "bg-white"} px-[18px] py-1`}>
    {rows.map((r, i) => {
      const rule = i < rows.length - 1 ? "border-b border-bm-mutedbg" : "";
      const text = (
        <div className={r.pairs ? "flex min-w-0 flex-1 flex-col gap-[3px]" : "flex min-w-0 flex-col gap-[3px]"} style={textWidth && !r.pairs ? { width: textWidth } : undefined}>
          <div className={`bm-label-l ${locked ? "text-bm-tertiary" : "text-bm-ink"}`}>{r.title}</div>
          {r.sub && <div className={`bm-body-s ${locked ? "text-bm-tertiary" : "text-bm-muted"}`}>{r.sub}</div>}
        </div>
      );
      if (r.pairs) {
        return (
          <div key={r.title} className={`flex h-[105px] items-center gap-[14px] py-4 ${rule}`}>
            {text}
            {/* At least 122px (1690:1234); wider when a half reads "Not yet" (no metrics). */}
            <div className="flex min-w-[122px] shrink-0 flex-col gap-[3px]">
              {r.pairs.map((p) => (
                <div key={p.label} className="flex items-center justify-between gap-2">
                  <span className="bm-body-s text-bm-ink">{p.label}</span>
                  <span className="ob-num-m text-right text-bm-ink">{p.value}</span>
                </div>
              ))}
            </div>
          </div>
        );
      }
      if (r.fits) {
        return (
          <div key={r.title} className={`flex h-[105px] items-center justify-between gap-4 py-4 ${rule}`}>
            {text}
            <div className="flex w-[122px] shrink-0 flex-col items-end gap-2">
              {r.fits.map((f) => (
                <span key={f} className="flex h-5 items-center gap-2 bm-label-l text-bm-ink"><span aria-hidden="true" className="h-[10px] w-[10px] shrink-0 rounded-full bg-bm-positive" />{f}</span>
              ))}
            </div>
          </div>
        );
      }
      return (
        <div key={r.title} className={`flex ${locked ? "h-[99px]" : "h-[105px]"} items-center justify-between gap-4 py-4 ${rule} ${muted ? "opacity-50" : ""}`}>
          {text}
          <div className="flex shrink-0 items-center gap-[10px]">
            {r.badge && <DarkBadge upper={badgeUpper}>{r.badge}</DarkBadge>}
            {r.warn && <WarnIcon />}
            {r.alert && <WarnTriangle />}
            {r.value != null && <span className="ob-num-m text-bm-ink">{r.value}</span>}
            {locked ? <LockIcon /> : r.chevron && <span className="bm-body-l text-bm-tertiary">›</span>}
          </div>
        </div>
      );
    })}
  </div>
);

// `rest` reaches the button (role="radio" with aria-checked inside a
// radiogroup, disabled).
export const OptionCard = ({ selected, title, sub, badge, onClick, className = "", children, ...rest }) => (
  <button
    type="button"
    onClick={onClick}
    {...rest}
    className={`flex w-full items-start gap-[14px] rounded-[12px] bg-white p-4 text-left disabled:cursor-not-allowed disabled:opacity-60 ${selected ? "border-2 border-bm-orange" : "border border-bm-border hover:border-bm-strong"} ${className}`}
  >
    {selected ? <img src={radioChecked20} alt="" className="h-5 w-5 shrink-0" /> : <span className="h-5 w-5 shrink-0 rounded-full border-[1.5px] border-bm-strong bg-white" />}
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <div className="bm-h-s text-bm-ink">{title}</div>
      {sub && <div className="bm-body-m text-bm-muted">{sub}</div>}
      {badge && <span><OutlineBadge dot="orange">{badge}</OutlineBadge></span>}
      {children}
    </div>
  </button>
);

export const PlanCard = ({ selected, title, sub, price, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex w-full flex-col gap-[10px] rounded-[12px] p-[18px] text-left ${selected ? "border-2 border-bm-orange bg-bm-subtle" : "border border-bm-border bg-white hover:border-bm-strong"}`}
  >
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-[10px]">
        {selected ? <img src={radioChecked18} alt="" className="h-[18px] w-[18px]" /> : <span className="h-[18px] w-[18px] rounded-full border-[1.5px] border-bm-strong bg-white" />}
        <span className="bm-h-s text-bm-ink">{title}</span>
      </span>
      <span className={`ob-num-m ${selected ? "text-bm-orange" : "text-bm-ink"}`}>{price}</span>
    </div>
    <span className="bm-body-m text-bm-muted">{sub}</span>
  </button>
);

// The shared V2 segmented control with this step's fixed 167px segments;
// on phones the segments share the track (two 167px segments overflow a
// 360px screen).
export const Segmented = (props) => {
  const phone = usePhoneTheme();
  return <SegmentedControl segmentClassName={phone ? "flex-1" : "w-[167px]"} {...props} />;
};

export const Toggle = ({ on, onChange, disabled }) => (
  <button type="button" role="switch" aria-checked={on} disabled={disabled} onClick={() => onChange(!on)} className={`relative h-7 w-[46px] shrink-0 rounded-full transition-colors ${on ? "bg-bm-orange" : "bg-bm-mutedbg"} ${disabled ? "opacity-60" : ""}`}>
    <span className={`absolute top-[3px] h-[22px] w-[22px] rounded-full bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.12)] transition-all ${on ? "left-[21px]" : "left-[3px]"}`} />
  </button>
);

// Spinner MD (56px, a 5px ring; smaller sizes reuse it). Size 96 is the
// Spinner LG of the phone import screen (1676:1053): a 7px ring and its own
// 96px arc, drawn at stroke 7 rather than a scaled-up MD arc.
export const Spinner = ({ size = 56 }) => {
  const lg = size >= 96;
  return (
    <span className="relative inline-block" style={{ width: size, height: size }}>
      <span className={lg ? "absolute inset-0 rounded-full border-[7px] border-bm-mutedbg" : "absolute inset-0 rounded-full border-[5px] border-bm-mutedbg"} />
      <img src={lg ? spinnerArc96 : spinnerArc} alt="" className="absolute inset-0 animate-spin" style={{ width: size, height: size }} />
    </span>
  );
};

// A green check circle. Size 64 (the phone confirm card, 1692:1307) carries
// the 28x20 check; smaller sizes the 11x8 one.
export const CheckCircle = ({ size = 22 }) => (
  <span className="flex items-center justify-center rounded-full bg-bm-positive" style={{ width: size, height: size }}>{size >= 64 ? <img src={check28} alt="" className="h-5 w-7" /> : <img src={checkSmall} alt="" className="h-2 w-[11px]" />}</span>
);

export const fmtK = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(Math.round(v));
};

// Shown in place of a number that is not there yet (never an em dash).
export const EMPTY = "Not yet";

export const fmtMoney = (n) => {
  if (n == null || n === "" || typeof n === "object") return EMPTY;
  const v = Number(n);
  return Number.isFinite(v) ? `$${Math.round(v).toLocaleString()}` : EMPTY;
};

// CacheValue and CacheNILx are stored as ranges ({min, max} or {from, to}).
export const fmtRange = (r) => {
  if (r == null || typeof r !== "object") return fmtMoney(r);
  const lo = r.min ?? r.from;
  const hi = r.max ?? r.to;
  if (lo == null || hi == null || Number(lo) === Number(hi)) return fmtMoney(lo ?? hi);
  return `${fmtMoney(lo)} to ${fmtMoney(hi)}`;
};

// Social network links only. The same list also holds sports links (MaxPreps,
// Hudl, Passport), which say nothing about visibility.
const SOCIAL_HOST = /(^|\.)(instagram\.com|tiktok\.com|youtube\.com|youtu\.be|x\.com|twitter\.com)$/i;
export const isSocialLink = (url) => {
  const raw = String(url || "").trim();
  if (!raw) return false;
  try {
    return SOCIAL_HOST.test(new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`).hostname);
  } catch {
    return false;
  }
};
export const socialLinks = (state) => (state?.socialMedia || []).filter(isSocialLink);
export const hasSocials = (state) => (state?.social?.score || 0) > 0 || socialLinks(state).length > 0;
// A saved link is not visibility until something was read from it: a score,
// or followers for a platform. A private or unreadable account never is.
export const visibilityRead = (state) => (state?.social?.score || 0) > 0 || Object.keys(state?.social?.platforms || {}).length > 0;
// Linked, but nothing read yet (still reading, or private).
export const visibilityUnread = (state) => hasSocials(state) && !visibilityRead(state);
export const UNREAD_COPY = "Your visibility shows once we can read your account.";
// The Visibility half of a score card.
export const visibilityHalf = (state) => {
  if (visibilityRead(state)) return { state: "on", note: "From your socials" };
  return { state: "warn", note: hasSocials(state) ? "Not read yet" : "Not connected" };
};
export const socialCount = (state) => Object.keys(state?.social?.platforms || {}).length || socialLinks(state).length;
export const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// US postal abbreviations (50 states plus DC), for the state pickers that do
// not have the state codes loaded. The server takes an abbreviation as well
// as a code.
export const US_STATE_ABBRS = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
